import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const FormActionMenu = ({
    onDuplicate,
    onEdit,
    onShare,
    onSchedule,
    onClose,
    status,
    scheduledCloseAt
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const buttonRef = useRef(null);
    const menuRef = useRef(null);

    const updatePosition = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const MENU_HEIGHT_APPROX = 300; // Estimated max height

            // Default: Position below
            let newTop = rect.bottom + 8;
            let newBottom = 'auto';

            // If not enough space below, position above
            if (spaceBelow < MENU_HEIGHT_APPROX) {
                newTop = 'auto';
                newBottom = window.innerHeight - rect.top + 8; // Distance from bottom + gap
            }

            setMenuPosition({
                top: newTop,
                left: rect.right - 200, // Align right edge (200px width)
                bottom: newBottom
            });
        }
    };

    const toggleMenu = (e) => {
        e.stopPropagation(); // Prevent card clicks if any
        if (!isOpen) {
            updatePosition();
        }
        setIsOpen(!isOpen);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isOpen &&
                buttonRef.current && !buttonRef.current.contains(event.target) &&
                menuRef.current && !menuRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        const handleScroll = () => {
            if (isOpen) setIsOpen(false);
        };

        const handleResize = () => {
            if (isOpen) setIsOpen(false);
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen]);

    const handleAction = (action) => {
        action();
        setIsOpen(false);
    };

    const isPublished = status === 'PUBLISHED';
    const isClosed = status === 'CLOSED';
    const isDraft = status === 'DRAFT';

    const menuContent = (
        <div
            ref={menuRef}
            className="position-fixed bg-white rounded-3 shadow-lg border border-light py-2"
            style={{
                width: '200px',
                zIndex: 9999, // High z-index to sit on top of everything
                top: menuPosition.top !== 'auto' ? `${menuPosition.top}px` : 'auto',
                left: `${menuPosition.left}px`,
                bottom: menuPosition.bottom !== 'auto' ? `${menuPosition.bottom}px` : 'auto'
            }}
        >
            {/* Duplicate - Always available */}
            <button
                onClick={() => handleAction(onDuplicate)}
                className="dropdown-item px-3 py-2 d-flex align-items-center text-secondary w-100 text-start bg-white border-0 hover-bg-light"
            >
                <i className="fa fa-clone me-3 text-muted" aria-hidden="true" style={{ width: '16px' }}></i>
                Duplicate
            </button>

            {/* Edit - Only available for Draft */}
            {isDraft && (
                <button
                    onClick={() => handleAction(onEdit)}
                    className="dropdown-item px-3 py-2 d-flex align-items-center text-secondary w-100 text-start bg-white border-0 hover-bg-light"
                >
                    <i className="fa fa-pencil me-3 text-muted" aria-hidden="true" style={{ width: '16px' }}></i>
                    Edit
                </button>
            )}

            <div className="dropdown-divider my-1 border-top opacity-50"></div>

            {isPublished && (
                <>
                    <button
                        onClick={() => handleAction(onShare)}
                        className="dropdown-item px-3 py-2 d-flex align-items-center text-secondary w-100 text-start bg-white border-0 hover-bg-light"
                    >
                        <i className="fa fa-share-alt me-3 text-muted" aria-hidden="true" style={{ width: '16px' }}></i>
                        Share
                    </button>

                    <button
                        onClick={() => handleAction(onSchedule)}
                        className={`dropdown-item px-3 py-2 d-flex align-items-center w-100 text-start bg-white border-0 hover-bg-light ${scheduledCloseAt ? 'text-warning' : 'text-secondary'}`}
                    >
                        <i className={`fa fa-clock-o me-3 ${scheduledCloseAt ? 'text-warning' : 'text-muted'}`} aria-hidden="true" style={{ width: '16px' }}></i>
                        {scheduledCloseAt ? 'Manage Schedule' : 'Schedule Closure'}
                    </button>

                    <div className="dropdown-divider my-1 border-top opacity-50"></div>

                    <button
                        onClick={() => handleAction(onClose)}
                        className="dropdown-item px-3 py-2 d-flex align-items-center text-danger w-100 text-start bg-white border-0 hover-bg-light"
                    >
                        <i className="fa fa-ban me-3 text-danger" aria-hidden="true" style={{ width: '16px' }}></i>
                        Close Form
                    </button>
                </>
            )}
        </div>
    );

    return (
        <>
            <button
                ref={buttonRef}
                onClick={toggleMenu}
                className="btn btn-light text-secondary fw-medium p-2 rounded-circle d-flex align-items-center justify-content-center hover-bg-gray-200"
                title="More Actions"
                style={{ width: '40px', height: '40px' }}
            >
                <i className="fa fa-ellipsis-v" aria-hidden="true"></i>
            </button>
            {isOpen && createPortal(menuContent, document.body)}
        </>
    );
};

export default FormActionMenu;
